/**
 * Letters Management Component
 * Welcome letters and email templates for partners
 */
import { useState } from "react";
import { useAuth } from "@/App";
import { 
  FileText, 
  Eye, 
  ExternalLink, 
  Mail, 
  Check, 
  ChevronDown 
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LettersManagement() {
  const { token } = useAuth();
  const [copiedId, setCopiedId] = useState(null);

  const letters = [
    {
      id: "hospital",
      title: "Hospital Partner Welcome Letter",
      description: "Introduction letter for hospital partners explaining AskIsaac platform benefits",
      audience: "Hospital Partners",
      url: "/welcome",
      color: "from-cyan-600 to-blue-600",
      bgColor: "bg-cyan-50",
      borderColor: "border-cyan-200"
    },
    {
      id: "supplier",
      title: "Supplier Partner Welcome Letter",
      description: "Introduction letter for supplier partners explaining the quote and RFQ system",
      audience: "Supplier Partners",
      url: "/welcome/supplier",
      color: "from-purple-600 to-indigo-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      mailto: {
        subject: "Welcome to the Sterile Mate / AskIsaac Supplier Network",
        body: `Hi {{supplier_name}},

Welcome to the Sterile Mate / AskIsaac supplier network — thank you for joining us.

A few things you can do right away:
  1. Sign in to your supplier portal at https://sterilemate.com/login
  2. Upload your W-9, certificate of insurance, and any manufacturing / distribution certifications so we can unlock higher-tier RFQs for you
  3. Reply to this email with any questions about scope, pricing guardrails, or payout timing

When a hospital posts a backorder in your category, you'll get an instant RFQ notification with a passwordless quote link. The lowest qualifying quote wins and Sterile Mate LLC issues the PO directly to you — no chasing the end customer.

Welcome aboard,

—
REVOLUTIONIZING HYGIENE IN HEALTHCARE

George Broughton III
President — Sterile Mate LLC
396 W Greens Road Suite 1139
Houston, TX 77067
Phone: 800.988.3892   Direct: 281.456.3458   Mobile: 323.243.9139
Email: george.broughton@sterilemate.com
Book a free 15-min Healthcare Revenue-Gap Audit: https://www.sterilemate.com/audit`
      }
    },
    {
      id: "contractor",
      title: "Contractor Partner Welcome Letter",
      description: "Introduction letter for contractor partners explaining job dispatch and portal features",
      audience: "Contractor Partners",
      url: "/welcome/contractor",
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200"
    },
    {
      id: "rfq_lbj_opc_sinks",
      title: "RFQ — LBJ OPC 3rd Floor Sinks",
      description: "Request for Quote to general contractors: 3-sink countertop/unit replacement at Lyndon B. Johnson OPC 3rd Floor patient area. Includes reference photos, Option 1 (countertop only) and Option 2 (full replacement).",
      audience: "General Contractors",
      url: `${API}/api/email-templates/rfq_lbj_opc_sinks/preview`,
      color: "from-blue-600 to-indigo-700",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      isExternalUrl: true,
      mailto: {
        subject: "Request for Quote — Countertop / Sink Replacement, LBJ OPC 3rd Floor",
        body: `Hi,

Attached / linked is the Request for Quote for the countertop replacement and sink unit upgrade at the Lyndon B. Johnson OPC 3rd Floor patient area.

Full RFQ with reference photos and both pricing options (countertop only / full replacement) is here:
${API}/api/email-templates/rfq_lbj_opc_sinks/preview

Project: Countertop Replacement / Unit Upgrade – 3rd Floor Patient Area
Location: Lyndon B. Johnson OPC, 5656 Kelly Street, Houston, TX 77026
Quote Due Date: Friday, May 1, 2026

A mandatory site walkthrough is required. Please reply to schedule.

Thanks,
George Broughton III
Founder & CEO, Sterile Mate LLC
george.broughton@sterilemate.com`
      }
    }
  ];

  const copyEmailHtml = async (templateId) => {
    try {
      const response = await fetch(`${API}/api/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.html) {
        await navigator.clipboard.writeText(data.html);
        setCopiedId(templateId);
        toast.success("Email HTML copied to clipboard!");
        setTimeout(() => setCopiedId(null), 2000);
      }
    } catch (error) {
      toast.error("Failed to copy email template");
    }
  };

  const downloadEmailHtml = async (templateId, templateName) => {
    try {
      const response = await fetch(`${API}/api/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.html) {
        const blob = new Blob([data.html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${templateId}_welcome_email.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Email template downloaded!");
      }
    } catch (error) {
      toast.error("Failed to download email template");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-manrope">Letters & Templates</h2>
          <p className="text-slate-500 text-sm">Welcome letters and email templates for partners</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {letters.map((letter) => (
          <div 
            key={letter.id} 
            className={`${letter.bgColor} ${letter.borderColor} border rounded-xl overflow-hidden hover:shadow-lg transition-shadow`}
          >
            {/* Header gradient */}
            <div className={`bg-gradient-to-r ${letter.color} px-6 py-4`}>
              <FileText className="w-8 h-8 text-white mb-2" />
              <h3 className="text-lg font-semibold text-white">{letter.title}</h3>
              <span className="text-xs text-white/80 font-medium">{letter.audience}</span>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <p className="text-slate-600 text-sm mb-4">{letter.description}</p>
              
              {/* Web Letter Actions */}
              <div className="flex gap-2 mb-3">
                <a
                  href={letter.isExternalUrl ? letter.url : letter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                  data-testid={`view-letter-${letter.id}`}
                >
                  <Eye className="w-4 h-4" />
                  {letter.isExternalUrl ? "Preview RFQ" : "View Web"}
                </a>
                <a
                  href={letter.isExternalUrl ? letter.url : letter.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {/* Mailto shortcut for RFQ/custom-send templates */}
              {letter.mailto && (
                <a
                  href={`mailto:?subject=${encodeURIComponent(letter.mailto.subject)}&body=${encodeURIComponent(letter.mailto.body)}`}
                  className="mb-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                  data-testid={`mailto-letter-${letter.id}`}
                >
                  <Mail className="w-4 h-4" />
                  Open in My Outlook (new email)
                </a>
              )}
              
              {/* Email Template Actions */}
              <div className="border-t border-slate-200 pt-3 mt-3">
                <p className="text-xs text-slate-500 mb-2 font-medium">Email Template (Outlook/Mailchimp)</p>
                <div className="flex gap-2 mb-2">
                  <a
                    href={`${API}/api/email-templates/${letter.id}/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Preview for Outlook
                  </a>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyEmailHtml(letter.id)}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      copiedId === letter.id 
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-200" 
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {copiedId === letter.id ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Copy HTML
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => downloadEmailHtml(letter.id, letter.title)}
                    className="inline-flex items-center justify-center px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
                    title="Download HTML file"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Instructions Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Using Email Templates
        </h3>
        <div className="space-y-4 text-sm text-slate-700">
          <div>
            <p className="font-semibold text-slate-900 mb-2">📧 For Outlook:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click <strong>&quot;Preview for Outlook&quot;</strong> - opens email in new tab</li>
              <li>Press <strong>Ctrl+A</strong> (Select All) then <strong>Ctrl+C</strong> (Copy)</li>
              <li>Go to Outlook, create new email, and <strong>Ctrl+V</strong> (Paste)</li>
              <li>The formatting will be preserved!</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-2">📨 For Mailchimp:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click <strong>&quot;Copy HTML&quot;</strong> on the template</li>
              <li>In Mailchimp, create campaign → <strong>&quot;Code your own&quot;</strong></li>
              <li>Paste the HTML directly into the editor</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Web URLs Section */}
      <div className="bg-slate-100 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <ExternalLink className="w-5 h-5 text-slate-600" />
          Direct Web Links
        </h3>
        <p className="text-slate-600 text-sm mb-4">
          Share these URLs directly with partners to view the letters online:
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-3">
            <span className="font-medium text-slate-700 w-24">Hospitals:</span>
            <code className="bg-white px-3 py-1 rounded border border-slate-200 text-slate-600 flex-1">
              {window.location.origin}/welcome
            </code>
          </li>
          <li className="flex items-center gap-3">
            <span className="font-medium text-slate-700 w-24">Suppliers:</span>
            <code className="bg-white px-3 py-1 rounded border border-slate-200 text-slate-600 flex-1">
              {window.location.origin}/welcome/supplier
            </code>
          </li>
          <li className="flex items-center gap-3">
            <span className="font-medium text-slate-700 w-24">Contractors:</span>
            <code className="bg-white px-3 py-1 rounded border border-slate-200 text-slate-600 flex-1">
              {window.location.origin}/welcome/contractor
            </code>
          </li>
        </ul>
      </div>
    </div>
  );
}
